import Card from "@rio-cloud/rio-uikit/Card";
import { Bar } from "@rio-cloud/rio-uikit";
import ComposedChart from "@rio-cloud/rio-uikit/ComposedChart";
import ChartTooltip from "@rio-cloud/rio-uikit/ChartTooltip";
import { useT } from "@hooks";

export interface ConcessionariaExpenseItem {
  concessionaria: string;
  expense: number;
}

interface TopConcessionariasChartProps {
  data: ConcessionariaExpenseItem[];
}

const PRIMARY_COLOR = "#30B4C0";

const formatAxisCurrency = (value: number): string => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  return `R$ ${value.toLocaleString("pt-BR")}`;
};

const MAX_CHARS_PER_LINE = 14;
const LINE_HEIGHT = 14;

const wrapLabelLines = (label: string): string[] => {
  const words = String(label ?? "")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return [""];
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    if (!current) {
      current = word;
      return;
    }
    if (current.length + 1 + word.length <= MAX_CHARS_PER_LINE) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  });
  lines.push(current);
  return lines;
};

const WrappedAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const lines = wrapLabelLines(payload?.value ?? "").map((text, index) => ({
    key: `concessionaria-tick-line-${index}`,
    text,
    dy: index === 0 ? 12 : LINE_HEIGHT,
  }));
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#A7AFBB" fontSize={12}>
        {lines.map((line) => (
          <tspan key={line.key} x={0} dy={line.dy}>
            {line.text}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const buildTooltipContent = (t: ReturnType<typeof useT>) => (props: any) => {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const item = props.payload[0].payload as ConcessionariaExpenseItem;
  return (
    <div className="display-flex flex-column gap-5 padding-y-10 padding-x-15 rounded border bg-lightest text-center text-size-12">
      <span className="text-medium">{item.concessionaria}</span>
      <span>
        {t("tolls.chart.tooltip.expense")} R${" "}
        {item.expense.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
};

export default function TopConcessionariasChart({ data }: Readonly<TopConcessionariasChartProps>) {
  const t = useT();

  const chartData = [...data]
    .sort((a, b) => a.expense - b.expense)
    .map((item) => ({
      concessionaria: item.concessionaria,
      expense: item.expense,
    }));

  const maxLabelLines = chartData.reduce((max, item) => Math.max(max, wrapLabelLines(item.concessionaria).length), 1);
  const xAxisHeight = maxLabelLines * LINE_HEIGHT + 16;
  const barsAreaHeight = 300;
  const chartTotalHeight = barsAreaHeight + xAxisHeight;

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm display-flex flex-column overflow-x-auto">
      <div className="padding-bottom-20">
        <span className="text-size-20 text-bold">{t("tolls.chart.topConcessionaires")}</span>
      </div>
      <div className="width-1000 width-100pct-md" style={{ height: chartTotalHeight }}>
        <ComposedChart
          height={chartTotalHeight}
          data={chartData}
          bars={[<Bar key="expense" dataKey="expense" barSize={90} radius={[4, 4, 0, 0]} fill={PRIMARY_COLOR} />]}
          tooltip={<ChartTooltip content={buildTooltipContent(t)} />}
          showXAxis
          showYAxis
          showGrid={false}
          xAxisOptions={{
            dataKey: "concessionaria",
            axisLine: { stroke: "#E0E0E0", strokeWidth: 1 },
            tickLine: false,
            padding: { left: 0, right: 0 },
            interval: 0,
            height: xAxisHeight,
            tick: WrappedAxisTick,
          }}
          yAxisOptions={{
            axisLine: false,
            tickLine: false,
            tickFormatter: formatAxisCurrency,
            width: 80,
          }}
          containerOptions={{ width: "100%", height: "100%" }}
        />
      </div>
    </Card>
  );
}
